import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  UserCircle2,
  Briefcase,
  Target,
  Sparkles,
  Bell,
  ShieldCheck,
  CreditCard,
  Loader2,
  Check,
  Download,
  Trash2,
  LogOut,
  AlertTriangle,
  KeyRound,
  ExternalLink,
  Mic,
} from "lucide-react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

/* ----------------------------- primitives ----------------------------- */

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
        disabled
          ? "bg-zinc-800 cursor-not-allowed opacity-60"
          : checked
          ? "bg-amber-500"
          : "bg-zinc-700 hover:bg-zinc-600"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-zinc-50 shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1.5 leading-relaxed">{hint}</div>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", testid, disabled }) {
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testid}
      className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700 disabled:opacity-60"
    />
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-zinc-950">
          {o.label}
        </option>
      ))}
    </select>
  );
}

function RadioPill({ label, description, checked, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick()}
      data-testid={testid}
      disabled={disabled}
      className={`text-left p-3 rounded-lg border transition-all ${
        disabled
          ? "border-zinc-800 bg-zinc-900/20 opacity-50 cursor-not-allowed"
          : checked
          ? "border-amber-500/50 bg-amber-500/[0.06] shadow-[0_0_20px_-8px_rgba(245,158,11,0.4)]"
          : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${checked ? "text-amber-300" : "text-zinc-100"}`}>{label}</span>
        <span
          className={`h-3.5 w-3.5 rounded-full border flex items-center justify-center ${
            checked ? "border-amber-400 bg-amber-500/20" : "border-zinc-700"
          }`}
        >
          {checked && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />}
        </span>
      </div>
      {description && <div className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{description}</div>}
    </button>
  );
}

function SavedBadge({ visible }) {
  if (!visible) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
      <Check className="h-3 w-3" /> Saved
    </span>
  );
}

function ErrorLine({ text }) {
  if (!text) return null;
  return (
    <div className="text-[12px] text-rose-400 flex items-start gap-1.5 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}

function SettingsCard({ id, icon: Icon, title, subtitle, open, onToggle, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 overflow-hidden transition-colors hover:border-zinc-800"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        aria-controls={id}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3.5 min-w-0">
          <span className="h-9 w-9 rounded-lg bg-zinc-800/70 border border-zinc-800 flex items-center justify-center flex-shrink-0">
            <Icon className="h-4 w-4 text-amber-400" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-50 tracking-tight">{title}</div>
            {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        <ChevronDown
          className={`h-4 w-4 text-zinc-500 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={id}
        className={`grid transition-all duration-300 ease-out ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5 pt-1 border-t border-zinc-800/70">{children}</div>
        </div>
      </div>
    </div>
  );
}

function SaveButton({ onClick, saving, testid, label = "Save changes" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saving}
      data-testid={testid}
      className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60 shadow-[0_0_20px_-6px_rgba(245,158,11,0.5)] transition-all"
    >
      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      {saving ? "Saving…" : label}
    </button>
  );
}

function GhostButton({ onClick, children, testid, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-2 text-sm rounded-lg px-4 py-2 border transition-colors disabled:opacity-50 ${
        danger
          ? "border-rose-500/30 text-rose-300 hover:bg-rose-500/10 hover:border-rose-500/50"
          : "border-zinc-800 text-zinc-200 hover:bg-zinc-900 hover:border-zinc-700"
      }`}
    >
      {children}
    </button>
  );
}

/* ----------------------------- main panel ----------------------------- */

const CAREER_LANGUAGES = ["English", "Spanish", "French", "German", "Portuguese", "Hindi", "Mandarin", "Japanese"];
const INTERVIEW_GOALS = [
  { value: "", label: "Select a goal" },
  { value: "new_job", label: "Land a new job" },
  { value: "career_switch", label: "Switch industries or roles" },
  { value: "promotion", label: "Prepare for internal promotion" },
  { value: "practice", label: "Sharpen interview skills" },
];

export default function SettingsPanel() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState({
    account: true,
    career: false,
    career_goal: false,
    preferences: false,
    notifications: false,
    privacy: false,
    subscription: false,
  });
  const toggle = (k) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  // ---- Account ----
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [savedName, setSavedName] = useState(false);
  const [nameErr, setNameErr] = useState("");

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [savedPwd, setSavedPwd] = useState(false);
  const [pwdErr, setPwdErr] = useState("");

  const [confirmDelete, setConfirmDelete] = useState("");
  const [deletingAcct, setDeletingAcct] = useState(false);
  const [deleteAcctErr, setDeleteAcctErr] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // ---- Career + Preferences + Notifications (persisted) ----
  const [prefs, setPrefs] = useState(null);
  const [savingCareer, setSavingCareer] = useState(false);
  const [savedCareer, setSavedCareer] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [savedGoal, setSavedGoal] = useState(false);
  const [goalErr, setGoalErr] = useState("");
  const [savingIP, setSavingIP] = useState(false);
  const [savedIP, setSavedIP] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);
  const [savedNotif, setSavedNotif] = useState(false);
  const [prefsErr, setPrefsErr] = useState("");

  // ---- Privacy ----
  const [clearingHistory, setClearingHistory] = useState(false);
  const [historyCleared, setHistoryCleared] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/user/preferences");
        setPrefs(data);
      } catch {
        setPrefs({
          career: { target_role: "", industry: "", years_of_experience: "", interview_goal: "", preferred_language: "English", career_goal: "", custom_career_goal: "" },
          interview: { coaching_style: "balanced", difficulty: "intermediate", voice_practice: false },
          notifications: { email_reminders: true, weekly_progress_summary: true, product_updates: false },
        });
      }
    })();
  }, []);

  const flash = (setter) => {
    setter(true);
    setTimeout(() => setter(false), 2500);
  };

  const saveName = async () => {
    setNameErr("");
    if (!name.trim()) {
      setNameErr("Name cannot be empty");
      return;
    }
    setSavingName(true);
    try {
      await api.post("/user/profile", { name: name.trim() });
      await refresh?.();
      flash(setSavedName);
    } catch (e) {
      setNameErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async () => {
    setPwdErr("");
    if (newPwd.length < 8) {
      setPwdErr("New password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      setPwdErr("Passwords do not match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/user/change-password", { current_password: currentPwd, new_password: newPwd });
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      flash(setSavedPwd);
    } catch (e) {
      setPwdErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingPwd(false);
    }
  };

  const deleteAccount = async () => {
    setDeleteAcctErr("");
    if (confirmDelete !== "DELETE") {
      setDeleteAcctErr("Type DELETE to confirm.");
      return;
    }
    setDeletingAcct(true);
    try {
      await api.delete("/user/account", { data: { confirm: "DELETE" } });
      window.location.replace("/");
    } catch (e) {
      setDeleteAcctErr(formatApiError(e?.response?.data?.detail));
      setDeletingAcct(false);
    }
  };

  const savePrefsSection = async (section, values, setSaving, setSaved) => {
    setPrefsErr("");
    setSaving(true);
    try {
      const { data } = await api.post("/user/preferences", { [section]: values });
      setPrefs(data);
      flash(setSaved);
    } catch (e) {
      setPrefsErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setCareerField = (k, v) => setPrefs((p) => ({ ...p, career: { ...p.career, [k]: v } }));
  const setIPField = (k, v) => setPrefs((p) => ({ ...p, interview: { ...p.interview, [k]: v } }));
  const setNotifField = (k, v) => setPrefs((p) => ({ ...p, notifications: { ...p.notifications, [k]: v } }));

  const saveCareerGoal = async () => {
    setGoalErr("");
    if (!prefs?.career?.career_goal) {
      setGoalErr("Select a career goal first.");
      return;
    }
    if (prefs.career.career_goal === "custom" && !(prefs.career.custom_career_goal || "").trim()) {
      setGoalErr("Please describe your custom career goal.");
      return;
    }
    setSavingGoal(true);
    try {
      const { data } = await api.post("/user/preferences", {
        career: {
          career_goal: prefs.career.career_goal,
          custom_career_goal:
            prefs.career.career_goal === "custom"
              ? (prefs.career.custom_career_goal || "").trim()
              : "",
        },
      });
      setPrefs(data);
      flash(setSavedGoal);
    } catch (e) {
      setGoalErr(formatApiError(e?.response?.data?.detail));
    } finally {
      setSavingGoal(false);
    }
  };

  const downloadData = () => {
    // Anchor download honors credentials cookie (same origin proxied via /api)
    const url = `${API_BASE}/user/data/export`;
    window.open(url, "_blank");
  };

  const clearHistory = async () => {
    if (!window.confirm("Delete all interview history? This permanently removes mock and video interview sessions, video/audio recordings, transcripts, question sets, reports, and Interview Day Briefs. This cannot be undone. Your resumes and account remain.")) return;
    setClearingHistory(true);
    try {
      await api.delete("/user/interview-history");
      flash(setHistoryCleared);
    } finally {
      setClearingHistory(false);
    }
  };

  const signOutAllDevices = async () => {
    if (!window.confirm("Sign out from all devices? You'll need to sign in again.")) return;
    setSigningOutAll(true);
    try {
      await logout?.();
      navigate("/login", { replace: true });
    } finally {
      setSigningOutAll(false);
    }
  };

  const isEmailAuth = (user?.provider || "email") === "email";

  return (
    <div data-testid="settings-panel" className="space-y-3 max-w-3xl">
      {/* -------- 1. Account -------- */}
      <SettingsCard
        id="acc"
        icon={UserCircle2}
        title="Account"
        subtitle="Your name, email, password, and account controls."
        open={open.account}
        onToggle={() => toggle("account")}
        testid="settings-account"
      >
        <div className="space-y-6 pt-4">
          {/* Name */}
          <div className="space-y-2">
            <Field label="Name">
              <TextInput value={name} onChange={setName} testid="settings-name-input" />
            </Field>
            <ErrorLine text={nameErr} />
            <div className="flex items-center gap-3">
              <SaveButton onClick={saveName} saving={savingName} testid="settings-name-save" label="Save name" />
              <SavedBadge visible={savedName} />
            </div>
          </div>

          {/* Email (read-only) */}
          <Field label="Email" hint="Contact support to change your email.">
            <TextInput value={user?.email || ""} onChange={() => {}} disabled testid="settings-email-input" />
          </Field>

          {/* Password */}
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Password</div>
            {isEmailAuth ? (
              <div className="space-y-2.5">
                <TextInput
                  type="password"
                  value={currentPwd}
                  onChange={setCurrentPwd}
                  placeholder="Current password"
                  testid="settings-pwd-current"
                />
                <TextInput
                  type="password"
                  value={newPwd}
                  onChange={setNewPwd}
                  placeholder="New password (min. 8 characters)"
                  testid="settings-pwd-new"
                />
                <TextInput
                  type="password"
                  value={confirmPwd}
                  onChange={setConfirmPwd}
                  placeholder="Confirm new password"
                  testid="settings-pwd-confirm"
                />
                <ErrorLine text={pwdErr} />
                <div className="flex items-center gap-3">
                  <SaveButton onClick={changePassword} saving={savingPwd} testid="settings-pwd-save" label="Update password" />
                  <SavedBadge visible={savedPwd} />
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-xs text-zinc-500 hover:text-zinc-300 underline underline-offset-4"
                    data-testid="settings-pwd-forgot"
                  >
                    Forgot current password?
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-xs text-zinc-500 flex items-center gap-2 p-3 rounded-lg border border-zinc-800 bg-zinc-950/60">
                <KeyRound className="h-3.5 w-3.5 text-zinc-500" />
                You&apos;re signed in via {user?.provider}. Manage your password through that provider.
              </div>
            )}
          </div>

          {/* Delete account */}
          <div className="pt-2 border-t border-zinc-800/70">
            <div className="text-[10px] uppercase tracking-widest text-rose-400 mb-2 mt-4">Delete account</div>
            <div className="text-xs text-zinc-500 mb-3 leading-relaxed">
              Permanently remove your account, resumes, mock sessions, and Interview Day Briefs. This cannot be undone.
            </div>
            {!showDelete ? (
              <GhostButton onClick={() => setShowDelete(true)} testid="settings-delete-open" danger>
                <Trash2 className="h-3.5 w-3.5" /> Delete my account
              </GhostButton>
            ) : (
              <div className="space-y-2.5 p-4 rounded-lg border border-rose-500/25 bg-rose-500/[0.04]">
                <div className="text-xs text-rose-200">
                  Type <span className="font-mono font-semibold">DELETE</span> below to confirm.
                </div>
                <TextInput
                  value={confirmDelete}
                  onChange={setConfirmDelete}
                  placeholder="DELETE"
                  testid="settings-delete-confirm"
                />
                <ErrorLine text={deleteAcctErr} />
                <div className="flex gap-2">
                  <button
                    onClick={deleteAccount}
                    disabled={deletingAcct}
                    data-testid="settings-delete-confirm-btn"
                    className="inline-flex items-center gap-2 text-sm bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-lg px-4 py-2 disabled:opacity-60"
                  >
                    {deletingAcct ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Permanently delete
                  </button>
                  <button
                    onClick={() => { setShowDelete(false); setConfirmDelete(""); setDeleteAcctErr(""); }}
                    className="text-sm text-zinc-400 hover:text-zinc-200 px-3"
                    data-testid="settings-delete-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </SettingsCard>

      {/* -------- 2. Career -------- */}
      {prefs && (
        <SettingsCard
          id="career"
          icon={Briefcase}
          title="Career"
          subtitle="Tailor question predictions and coaching to your role."
          open={open.career}
          onToggle={() => toggle("career")}
          testid="settings-career"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
            <Field label="Target Role">
              <TextInput
                value={prefs.career.target_role}
                onChange={(v) => setCareerField("target_role", v)}
                placeholder="e.g. Senior Product Manager"
                testid="settings-career-role"
              />
            </Field>
            <Field label="Industry">
              <TextInput
                value={prefs.career.industry}
                onChange={(v) => setCareerField("industry", v)}
                placeholder="e.g. Fintech, Healthcare, SaaS"
                testid="settings-career-industry"
              />
            </Field>
            <Field label="Years of Experience">
              <Select
                value={prefs.career.years_of_experience || ""}
                onChange={(v) => setCareerField("years_of_experience", v)}
                testid="settings-career-yoe"
                options={[
                  { value: "", label: "Select experience" },
                  { value: "0-1", label: "0–1 years" },
                  { value: "2-4", label: "2–4 years" },
                  { value: "5-8", label: "5–8 years" },
                  { value: "9-14", label: "9–14 years" },
                  { value: "15+", label: "15+ years" },
                ]}
              />
            </Field>
            <Field label="Interview Goal">
              <Select
                value={prefs.career.interview_goal || ""}
                onChange={(v) => setCareerField("interview_goal", v)}
                testid="settings-career-goal"
                options={INTERVIEW_GOALS}
              />
            </Field>
            <Field label="Preferred Language">
              <Select
                value={prefs.career.preferred_language || "English"}
                onChange={(v) => setCareerField("preferred_language", v)}
                testid="settings-career-language"
                options={CAREER_LANGUAGES.map((l) => ({ value: l, label: l }))}
              />
            </Field>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <SaveButton
              onClick={() => savePrefsSection("career", prefs.career, setSavingCareer, setSavedCareer)}
              saving={savingCareer}
              testid="settings-career-save"
            />
            <SavedBadge visible={savedCareer} />
          </div>
        </SettingsCard>
      )}

      {/* -------- 2b. Career Goal -------- */}
      {prefs && (
        <SettingsCard
          id="career-goal"
          icon={Target}
          title="Career Goal"
          subtitle="Guides personalization across resume, questions, coaching, and readiness."
          open={open.career_goal}
          onToggle={() => toggle("career_goal")}
          testid="settings-career-goal"
        >
          <div className="pt-4 space-y-4">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              What are you working toward?
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { value: "first_job", label: "Land my first job", description: "Early career, first full-time role." },
                { value: "new_job", label: "Get a new job", description: "Move to a comparable role elsewhere." },
                { value: "career_change", label: "Career change", description: "Pivot to a new function or industry." },
                { value: "promotion", label: "Promotion", description: "Level up internally or laterally." },
                { value: "executive", label: "Executive role", description: "Director, VP, or C-suite." },
                { value: "international", label: "International opportunity", description: "Relocation or cross-border role." },
                { value: "custom", label: "Custom", description: "Describe your own path." },
              ].map((opt) => (
                <label
                  key={opt.value}
                  data-testid={`settings-cgoal-${opt.value}`}
                  onClick={() => setCareerField("career_goal", opt.value)}
                  className={`cursor-pointer text-left p-3.5 rounded-lg border transition-all ${
                    prefs.career.career_goal === opt.value
                      ? "border-amber-500/50 bg-amber-500/[0.06] shadow-[0_0_20px_-8px_rgba(245,158,11,0.4)]"
                      : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                        prefs.career.career_goal === opt.value
                          ? "border-amber-400 bg-amber-500/20"
                          : "border-zinc-700"
                      }`}
                    >
                      {prefs.career.career_goal === opt.value && (
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div
                        className={`text-sm font-medium ${
                          prefs.career.career_goal === opt.value ? "text-amber-300" : "text-zinc-100"
                        }`}
                      >
                        {opt.label}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                        {opt.description}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {prefs.career.career_goal === "custom" && (
              <div
                data-testid="settings-cgoal-custom-wrap"
                className="p-4 rounded-lg border border-amber-500/25 bg-amber-500/[0.03] animate-in fade-in duration-200"
              >
                <Field
                  label="Describe your career goal"
                  hint="Examples: Become a Country Head · Transition to Investment Banking · Move into Product Management · Return to work after a career break."
                >
                  <TextInput
                    value={prefs.career.custom_career_goal || ""}
                    onChange={(v) => setCareerField("custom_career_goal", v)}
                    placeholder="e.g. Transition to Product Management at a fintech scale-up"
                    testid="settings-cgoal-custom-input"
                  />
                </Field>
              </div>
            )}

            <ErrorLine text={goalErr} />
            <div className="flex items-center gap-3">
              <SaveButton
                onClick={saveCareerGoal}
                saving={savingGoal}
                testid="settings-cgoal-save"
                label="Save career goal"
              />
              <SavedBadge visible={savedGoal} />
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed pt-1">
              Your goal shapes resume alignment, ATS checks, predicted questions, mock interview style,
              coaching tone, Readiness scoring, Interview Day Briefs, and recommended next steps.
            </div>
          </div>
        </SettingsCard>
      )}

      {/* -------- 3. Interview Preferences -------- */}
      {prefs && (
        <SettingsCard
          id="ip"
          icon={Sparkles}
          title="Interview Preferences"
          subtitle="How your AI coach behaves during practice."
          open={open.preferences}
          onToggle={() => toggle("preferences")}
          testid="settings-interview-prefs"
        >
          <div className="space-y-6 pt-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">AI Coaching Style</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { value: "supportive", label: "Supportive", description: "Encouraging, positive reinforcement." },
                  { value: "balanced", label: "Balanced", description: "Honest feedback with warmth." },
                  { value: "challenging", label: "Challenging", description: "Direct pressure, tough follow-ups." },
                ].map((opt) => (
                  <RadioPill
                    key={opt.value}
                    label={opt.label}
                    description={opt.description}
                    checked={prefs.interview.coaching_style === opt.value}
                    onClick={() => setIPField("coaching_style", opt.value)}
                    testid={`settings-coach-${opt.value}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Interview Difficulty</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { value: "beginner", label: "Beginner", description: "Foundational, warm-up questions." },
                  { value: "intermediate", label: "Intermediate", description: "Real-world scenarios, follow-ups." },
                  { value: "advanced", label: "Advanced", description: "Executive-level depth, edge cases." },
                ].map((opt) => (
                  <RadioPill
                    key={opt.value}
                    label={opt.label}
                    description={opt.description}
                    checked={prefs.interview.difficulty === opt.value}
                    onClick={() => setIPField("difficulty", opt.value)}
                    testid={`settings-diff-${opt.value}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex items-start gap-4 p-3.5 rounded-lg border border-zinc-800 bg-zinc-950/40">
              <span className="h-9 w-9 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center flex-shrink-0">
                <Mic className="h-4 w-4 text-zinc-500" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-zinc-300">Enable Voice Practice</span>
                  <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-full px-2 py-0.5">
                    Coming Soon
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">Practice out loud with your AI interviewer.</div>
              </div>
              <Toggle checked={false} onChange={() => {}} disabled testid="settings-voice-toggle" />
            </div>

            <div className="pt-1 flex items-center gap-3">
              <SaveButton
                onClick={() => savePrefsSection("interview", prefs.interview, setSavingIP, setSavedIP)}
                saving={savingIP}
                testid="settings-ip-save"
              />
              <SavedBadge visible={savedIP} />
            </div>
          </div>
        </SettingsCard>
      )}

      {/* -------- 4. Notifications -------- */}
      {prefs && (
        <SettingsCard
          id="notif"
          icon={Bell}
          title="Notifications"
          subtitle="Choose what lands in your inbox."
          open={open.notifications}
          onToggle={() => toggle("notifications")}
          testid="settings-notifications"
        >
          <div className="divide-y divide-zinc-800/70 pt-2">
            {[
              { key: "email_reminders", label: "Email reminders", description: "Gentle nudges before your upcoming interview." },
              { key: "weekly_progress_summary", label: "Weekly progress summary", description: "Your readiness delta, session count, and next focus." },
              { key: "product_updates", label: "Product updates", description: "Occasional emails on new features and improvements." },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between py-3.5 gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-zinc-100">{row.label}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{row.description}</div>
                </div>
                <Toggle
                  checked={!!prefs.notifications[row.key]}
                  onChange={(v) => setNotifField(row.key, v)}
                  testid={`settings-notif-${row.key}`}
                />
              </div>
            ))}
          </div>
          <div className="pt-4 flex items-center gap-3">
            <SaveButton
              onClick={() => savePrefsSection("notifications", prefs.notifications, setSavingNotif, setSavedNotif)}
              saving={savingNotif}
              testid="settings-notif-save"
            />
            <SavedBadge visible={savedNotif} />
          </div>
        </SettingsCard>
      )}

      {/* -------- 5. Privacy -------- */}
      <SettingsCard
        id="privacy"
        icon={ShieldCheck}
        title="Privacy"
        subtitle="Control your data and active sessions."
        open={open.privacy}
        onToggle={() => toggle("privacy")}
        testid="settings-privacy"
      >
        <div className="space-y-3 pt-4">
          <div className="flex items-center justify-between gap-4 p-3.5 rounded-lg border border-zinc-800 bg-zinc-950/40">
            <div className="min-w-0">
              <div className="text-sm text-zinc-100">Download my data</div>
              <div className="text-xs text-zinc-500 mt-0.5">Export profile, resumes, interview transcripts, sessions, reports, and briefs as a JSON file. Video files aren&apos;t embedded — replay them in-app.</div>
            </div>
            <GhostButton onClick={downloadData} testid="settings-privacy-export">
              <Download className="h-3.5 w-3.5" /> Download
            </GhostButton>
          </div>

          <div className="flex items-center justify-between gap-4 p-3.5 rounded-lg border border-zinc-800 bg-zinc-950/40">
            <div className="min-w-0">
              <div className="text-sm text-zinc-100">Delete interview history</div>
              <div className="text-xs text-zinc-500 mt-0.5">Permanently removes all interview sessions, video/audio recordings, transcripts, question sets, reports, and Interview Day Briefs. Resumes stay.</div>
            </div>
            <div className="flex items-center gap-3">
              <SavedBadge visible={historyCleared} />
              <GhostButton onClick={clearHistory} testid="settings-privacy-clear" danger disabled={clearingHistory}>
                {clearingHistory ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                Clear history
              </GhostButton>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 p-3.5 rounded-lg border border-zinc-800 bg-zinc-950/40">
            <div className="min-w-0">
              <div className="text-sm text-zinc-100">Sign out all devices</div>
              <div className="text-xs text-zinc-500 mt-0.5">Ends your session everywhere you&apos;re signed in.</div>
            </div>
            <GhostButton onClick={signOutAllDevices} testid="settings-privacy-signout-all" disabled={signingOutAll}>
              {signingOutAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
              Sign out all
            </GhostButton>
          </div>

          <ErrorLine text={prefsErr} />

          <div className="mt-2 pt-3 border-t border-zinc-800/70 text-xs text-zinc-500 leading-relaxed">
            Explore our full{" "}
            <a
              href="/trust"
              target="_blank"
              rel="noreferrer"
              data-testid="settings-privacy-trust-link"
              className="text-amber-400 hover:text-amber-300 underline underline-offset-4"
            >
              Trust &amp; Privacy center
            </a>{" "}
            for the Privacy Policy, Terms, Security, Cookies, and AI Transparency in one place.
          </div>
        </div>
      </SettingsCard>

      {/* -------- 6. Subscription -------- */}
      <SettingsCard
        id="sub"
        icon={CreditCard}
        title="Subscription"
        subtitle="Your current plan and billing."
        open={open.subscription}
        onToggle={() => toggle("subscription")}
        testid="settings-subscription"
      >
        <div className="pt-4 space-y-4">
          <div className="rounded-xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.05] via-zinc-900/40 to-zinc-950/60 p-5 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-widest text-amber-400">Manage billing</div>
              <div className="mt-1 text-2xl font-bold text-zinc-50">Plan &amp; Billing</div>
              <div className="text-sm text-zinc-400 mt-2 leading-relaxed">
                See your current plan, usage, and history. Buy the Interview Ready Pass, upgrade to Career Pro, or contact sales for Career Enterprise.
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/app/billing")}
                  data-testid="settings-sub-open-billing"
                  className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 shadow-[0_0_20px_-6px_rgba(245,158,11,0.5)]"
                >
                  Open Billing <ExternalLink className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  data-testid="settings-sub-see-plans"
                  className="inline-flex items-center gap-2 text-sm border border-zinc-800 text-zinc-200 hover:bg-zinc-900 hover:border-zinc-700 rounded-lg px-4 py-2"
                >
                  Compare plans
                </button>
              </div>
            </div>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}
